import { forwardRef } from "react";
import type { ReactNode } from "react";
import { cn } from "../../lib/cn";

export interface BottomNavItem {
  /** Stable identity — matched against BottomNav `value`. */
  key: string;
  label: string;
  /** Consumer-supplied icon node — colour inherits from the item's text token. */
  icon: ReactNode;
  /** Route hint for the caller — the component itself never routes. */
  to?: string;
  /** Extra callback fired alongside `onChange`. */
  onSelect?: () => void;
}

export interface BottomNavProps {
  items: BottomNavItem[];
  /** Key of the currently selected item. */
  value: string;
  onChange: (key: string) => void;
  className?: string;
}

interface BottomNavItemButtonProps {
  item: BottomNavItem;
  selected: boolean;
  onSelect: (key: string) => void;
}

function BottomNavItemButton({
  item,
  selected,
  onSelect,
}: BottomNavItemButtonProps) {
  return (
    <li className="flex-1">
      <button
        type="button"
        aria-label={item.label}
        aria-current={selected ? "page" : undefined}
        onClick={() => {
          if (item.onSelect) {
            item.onSelect();
          }
          onSelect(item.key);
        }}
        className={cn(
          "flex h-full w-full flex-col items-center justify-center gap-0.5 rounded-3xl type-caption transition-colors outline-none focus-visible:ring-2 focus-visible:ring-border-focused focus-visible:ring-inset",
          selected
            ? "font-semibold text-content-accent"
            : "text-content-secondary",
        )}
      >
        {item.icon}
        <span>{item.label}</span>
      </button>
    </li>
  );
}

/**
 * Fixed bottom tab bar. Router-free: callers navigate in `onChange` (the
 * `to` field is carried for their convenience only).
 */
export const BottomNav = forwardRef<HTMLElement, BottomNavProps>(
  ({ items, value, onChange, className }, ref) => {
    return (
      <nav
        ref={ref}
        className={cn(
          "fixed inset-x-0 bottom-0 z-(--z-nav) pl-[max(4px,env(safe-area-inset-left))] pr-[max(4px,env(safe-area-inset-right))] pb-[max(12px,env(safe-area-inset-bottom))]",
          className,
        )}
      >
        <div className="mx-auto w-full max-w-[600px] rounded-3xl border border-border-default bg-surface-canvas-translucent shadow-bottom-nav">
          <ul className="flex h-14">
            {items.map((item) => (
              <BottomNavItemButton
                key={item.key}
                item={item}
                selected={item.key === value}
                onSelect={onChange}
              />
            ))}
          </ul>
        </div>
      </nav>
    );
  },
);
BottomNav.displayName = "BottomNav";
